const BADGE_COLORS = [
  { background: "#e8f1fb", color: "#1d4e89" },
  { background: "#e7f6ee", color: "#1e7a46" },
  { background: "#fdeee6", color: "#9a3412" },
  { background: "#eef2ff", color: "#3730a3" },
  { background: "#f3e8ff", color: "#6b21a8" },
  { background: "#fce7f3", color: "#9d174d" }
]

export function humanizeField(fieldName) {
  if (!fieldName) {
    return ""
  }
  return String(fieldName).replace(/_/g, " ")
}

export function fieldLabel(caseType, fieldName) {
  if (!fieldName) {
    return ""
  }
  if (fieldName === "title" || fieldName === "filename" || fieldName === "content") {
    return fieldName
  }
  const fieldsets = caseType && caseType.currentSchema && caseType.currentSchema.fieldsets
    ? caseType.currentSchema.fieldsets
    : []
  let i
  for (i = 0; i < fieldsets.length; i++) {
    const fields = fieldsets[i].fields || []
    let j
    for (j = 0; j < fields.length; j++) {
      if (fields[j].name === fieldName && fields[j].display_name) {
        return String(fields[j].display_name).toLowerCase()
      }
    }
  }
  return humanizeField(fieldName)
}

export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

function wrapMatch(text) {
  return "<mark class=\"search-hit-mark\">" + text + "</mark>"
}

export function highlightHtml(raw, searchTerm) {
  if (!raw) {
    return ""
  }
  const text = String(raw)
  if (/<em[\s>]/i.test(text)) {
    let out = ""
    const re = /<em>([\s\S]*?)<\/em>/gi
    let last = 0
    let match = re.exec(text)
    while (match) {
      out += escapeHtml(text.substring(last, match.index))
      out += wrapMatch(escapeHtml(match[1]))
      last = re.lastIndex
      match = re.exec(text)
    }
    out += escapeHtml(text.substring(last))
    return out.replace(/\s+/g, " ").trim()
  }
  let escaped = escapeHtml(text).replace(/\s+/g, " ").trim()
  const term = String(searchTerm || "").trim()
  if (!escaped || !term) {
    return escaped
  }
  const tokens = term.split(/\s+/)
  let i
  for (i = 0; i < tokens.length; i++) {
    const token = tokens[i]
    if (!token || token.length < 2) {
      continue
    }
    const upper = token.toUpperCase()
    if (upper === "AND" || upper === "OR" || upper === "NOT") {
      continue
    }
    const safe = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    escaped = escaped.replace(new RegExp("(" + safe + ")", "gi"), wrapMatch("$1"))
  }
  return escaped
}

export function formatSize(bytes) {
  if (bytes === null || bytes === undefined || bytes === "") {
    return ""
  }
  const value = Number(bytes)
  if (isNaN(value) || value < 0) {
    return ""
  }
  if (value < 1024) {
    return value + " B"
  }
  if (value < 1024 * 1024) {
    return (value / 1024).toFixed(1).replace(/\.0$/, "") + " KB"
  }
  return (value / (1024 * 1024)).toFixed(1).replace(/\.0$/, "") + " MB"
}

export function summaryLine(filter, totals) {
  const counts = totals || {}
  if (filter === "documents") {
    const count = counts.documentMatchTotal || 0
    return count + (count === 1 ? " document" : " documents") + " across all cases"
  }
  if (filter === "cases") {
    const count = counts.caseMatchTotal || 0
    return count + (count === 1 ? " case" : " cases") + " across all case types"
  }
  const count = counts.matchTotal || 0
  return count + (count === 1 ? " result" : " results") + " across all case types"
}

export function badgeText(row) {
  if (!row) {
    return ""
  }
  if (row.kind === "document") {
    if (row.extension) {
      return String(row.extension).replace(/^\./, "").toLowerCase()
    }
    const name = row.filename || row.title || ""
    const parts = String(name).split(".")
    if (parts.length > 1) {
      return parts[parts.length - 1].toLowerCase()
    }
    return "file"
  }
  if (row.caseTypeCode) {
    return String(row.caseTypeCode).replace(/_/g, " ")
  }
  if (row.caseTypeName) {
    return String(row.caseTypeName).toLowerCase()
  }
  return "case"
}

export function badgeStyle(text) {
  const source = String(text || "")
  let hash = 0
  let i
  for (i = 0; i < source.length; i++) {
    hash = ((hash << 5) - hash) + source.charCodeAt(i)
    hash = hash & hash
  }
  const palette = BADGE_COLORS[Math.abs(hash) % BADGE_COLORS.length]
  return {
    backgroundColor: palette.background,
    color: palette.color
  }
}

export function quotedTitle(title) {
  const value = String(title || "").trim()
  if (!value) {
    return ""
  }
  if (value.indexOf(" ") >= 0) {
    return "\"" + value + "\""
  }
  return value
}

export function rowsFromLegacy(results, documents) {
  const rows = []
  const groups = results || []
  groups.forEach(function(group) {
    const cases = group.cases || []
    cases.forEach(function(kase) {
      rows.push({
        kind: "case",
        id: kase.id,
        caseId: kase.id,
        caseTypeId: group.caseTypeId,
        title: kase.title || group.displayName || "Case",
        caseTypeName: group.displayName,
        caseTypeCode: group.caseType && group.caseType.code ? group.caseType.code : "",
        iconSet: group.iconSet,
        iconCode: group.iconCode,
        currentState: kase.currentState || "",
        updatedAt: kase.updatedAt,
        matchedField: kase.matchedField || "",
        highlight: kase.highlight || "",
        latestActivity: kase.latestActivity || "",
        score: kase.score || 0
      })
    })
  })
  const docs = documents && documents.documents ? documents.documents : []
  docs.forEach(function(doc) {
    rows.push({
      kind: "document",
      id: doc.id,
      caseId: doc.caseId,
      caseTypeId: doc.caseTypeId,
      title: doc.filename,
      filename: doc.filename,
      extension: doc.extension,
      contentType: doc.contentType,
      size: doc.size,
      url: doc.url,
      caseTitle: doc.caseTitle,
      createdAt: doc.createdAt,
      matchedField: doc.matchedField || (doc.highlight ? "content" : "filename"),
      highlight: doc.highlight || "",
      score: doc.score || 0
    })
  })
  return rows
}

export function totalsFromLegacy(results, documents) {
  let caseMatchTotal = 0
  ;(results || []).forEach(function(group) {
    caseMatchTotal += group.total || 0
  })
  const documentMatchTotal = documents && documents.total ? documents.total : 0
  return {
    matchTotal: caseMatchTotal + documentMatchTotal,
    caseMatchTotal: caseMatchTotal,
    documentMatchTotal: documentMatchTotal
  }
}
